import { useCallback, useEffect, useState, useMemo } from 'react';
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from '@clerk/clerk-react';
import { Link, useLocation } from 'react-router-dom';
import { download, setAutoDownloadFlag, sendDownloadReminderEmail, checkAndClearAutoDownloadFlag } from '../../../utility/api';
import { usePavilion } from '../../contexts/PavilionContext';
import { useAudience } from '../../contexts/AudienceContext';
import { isMobileDevice, isNonWindowsOS } from '../../../utility/utility';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faUser, faComputer, faLock } from '@fortawesome/pro-solid-svg-icons';
import { useResponsive } from '../../../hooks/useResponsive';
import { Privacy } from '@playcastdotio/Settings';
import { ownerAliasState } from '../../../api/stores/lobbyStores';
import { authStore } from '../../../api/stores/globalStores';
import { getPersistentPlayerId } from '../../../api';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { determineProfileSlug, determinePlatformFromAliases } from '@playcastdotio/SharedComponents';
import config from '../../../config.json';
import { toast } from 'sonner';
import { sendAnalytics } from '@playcastdotio/Analytics';
import '../.././../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * AidaHeader Component
 * New header design from AidasCopy - Fixed position, black background
 * Preserves existing download button functionality with auth
 * Adds scroll-based transparency and animations
 */
export const AidaHeader = () => {
  const { isPavilionMode } = usePavilion();
  const { audience } = useAudience();
  const { scrollY } = useScroll();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isMobile } = useResponsive();
  const { user } = useUser();
  const [ownerAliasPlatformPrefix, setOwnerAliasPlatformPrefix] = useState<'pt' | 'p' | 'pd' | undefined>(undefined);
  const [ownerAlias, setOwnerAlias] = useState<string | undefined>(undefined);

  // Check if should show "Join" instead of "Download"
  const showJoinButton = isMobile || isNonWindowsOS();

  // Generate profile URL
  const getProfileUrl = useCallback(() => {
    if (!user) return undefined;

    const ownerAliases = ownerAliasState.currentState.aliases;
    const platform = determinePlatformFromAliases(ownerAliases) || IdentityPlatform.CLERK;

    const profileSlug = determineProfileSlug({
      authId: user.id,
      username: user.username ?? '',
      defaultAlias: ownerAliases.default,
      platform: platform,
    });

    return profileSlug ? `/${profileSlug}` : undefined;
  }, [user]);

  const profileUrl = useMemo(() => getProfileUrl(), [getProfileUrl]);

  // Transform scroll position to background opacity
  const headerBg = useTransform(
    scrollY,
    [0, 100],
    ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0)']
  );

  const navOpacity = useTransform(scrollY, [0, 120], [1, 0]);

  const handleDownloadClick = useCallback(() => {
    sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: showJoinButton ? 'Join' : 'Download', buttonLocation: 'AidaHeader' });
    setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS());
  }, [showJoinButton]);

  const handleSignedInDownloadClick = useCallback(async () => {
    sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: showJoinButton ? 'Join' : 'Download', buttonLocation: 'AidaHeader' });
    console.log('handleSignedInDownloadClick called, isMobileDevice():', isMobileDevice(), 'isNonWindowsOS():', isNonWindowsOS());

    // Check if user is on mobile device or non-Windows OS
    if (isMobileDevice() || isNonWindowsOS()) {
      // On mobile or non-Windows OS, send email reminder instead of downloading
      const email = user?.primaryEmailAddress?.emailAddress;
      console.log('Mobile/Non-Windows detected, email:', email);
      if (email) {
        await sendDownloadReminderEmail(email);
        toast.success('Download reminder email sent!', {
          description: 'Check your email for a link to download Playcast on your PC.',
          duration: 5000,
          style: {
            background: '#fbbf24',
            color: '#000000',
            border: 'none',
          },
        });
      } else {
        toast.error('Unable to send reminder', {
          description: 'Email address not found.',
          style: {
            background: '#fbbf24',
            color: '#000000',
            border: 'none',
          },
        });
      }
    } else {
      // On Windows desktop, proceed with normal download
      console.log('Windows desktop detected, calling download()');
      download();
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const listener = ownerAliasState.addListener((state) => {
      const platform = determinePlatformFromAliases(state.aliases);
      if (platform) {
        switch (platform) {
          case IdentityPlatform.CLERK:
            setOwnerAliasPlatformPrefix('p');
            break;
          case IdentityPlatform.DISCORD:
            setOwnerAliasPlatformPrefix('pd');
            break;
          case IdentityPlatform.TWITCH:
            setOwnerAliasPlatformPrefix('pt');
            break;
        }
        setOwnerAlias(state.aliases.default);
      }
    });

    return () => {
      ownerAliasState.removeListener(listener);
    };
  }, []);

  // Check for auto-download flag on component load (same as Header component)
  useEffect(() => {
    console.log('[AidaHeader] Auto-download check - user:', !!user);
    if (!user) {
      return;
    }

    // Add a small delay to ensure user object is fully loaded
    const timeoutId = setTimeout(async () => {
      const autoDownloadData = checkAndClearAutoDownloadFlag();
      console.log('[AidaHeader] Auto-download data:', autoDownloadData);
      if (autoDownloadData) {
        // Check if this was from a mobile device
        if (autoDownloadData.isMobile) {
          console.log('[AidaHeader] Mobile detected, sending email');
          // On mobile, send email reminder instead of downloading
          const email = user.primaryEmailAddress?.emailAddress;
          if (email) {
            await sendDownloadReminderEmail(email);
            toast.success('Download reminder email sent!', {
              description: 'Check your email for a link to download Playcast on your PC.',
              duration: 5000,
              style: {
                background: '#fbbf24',
                color: '#000000',
                border: 'none',
              },
            });
          } else {
            toast.error('Unable to send reminder', {
              description: 'Email address not found.',
              style: {
                background: '#fbbf24',
                color: '#000000',
                border: 'none',
              },
            });
          }
        } else {
          console.log('[AidaHeader] Desktop detected, downloading');
          // On desktop, proceed with normal download
          download();
        }
      }
    }, 100); // 100ms delay

    return () => clearTimeout(timeoutId);
  }, [user]);

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <>
    <motion.header
      data-aida-header
      className="fixed top-0 left-0 right-0 z-[400] transition-all duration-300"
      style={{
        backgroundColor: isMobile ? 'rgba(0, 0, 0, 1)' : headerBg,
        height: isMobile ? '47px' : '3.5vw',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        borderBottom: '1px solid transparent',
        width: '100%'
      }}
    >
      <div className="flex h-full items-center justify-between w-full" style={{ paddingLeft: isMobile ? '16px' : '1vw', paddingRight: isMobile ? '16px' : '1vw' }}>
        {/* Logo - Left Aligned */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
          <motion.div
            className="flex items-center cursor-pointer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <img
              src="/img/65bded82faa65b750f8fc6e4/5769455/image-ee2074f7-a9a6-45b8-b8b8-9c944d15b99f.png"
              alt="Playcast Logo"
              style={{
                height: isMobile ? '21px' : '1.2vw',
                width: 'auto',
                opacity: 1,
                objectFit: 'contain'
              }}
            />
          </motion.div>
        </Link>

        {/* Center Nav Links — desktop only */}
        {!isMobile && (
          <motion.nav
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.5vw',
              pointerEvents: 'none',
              opacity: navOpacity,
            }}
          >
            {[
              { label: 'Home', to: '/' },
              { label: 'For Hosts', to: audience ? `/hosts/${audience}` : '/hosts' },
              { label: 'For Developers', to: '/developers' },
              { label: 'About', to: '/about' },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.to}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.9vw',
                  letterSpacing: '-0.5px',
                  color: isActive(item.to) ? '#FBBF24' : 'rgba(255, 255, 255, 1)',
                  textDecoration: 'none',
                  fontVariationSettings: "'wght' 500",
                  whiteSpace: 'nowrap',
                  pointerEvents: 'auto',
                  borderBottom: isActive(item.to) ? '2px solid #FBBF24' : '2px solid transparent',
                  paddingBottom: '2px',
                }}
              >
                {item.label}
              </Link>
            ))}
          </motion.nav>
        )}

        {/* Download/Join Button and User Profile - Right Aligned */}
        <motion.div
          className="flex items-center"
          style={{ gap: isMobile ? '16px' : '1vw', alignItems: 'center' }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <SignedIn>
            <motion.button
              onClick={handleSignedInDownloadClick}
              className="aida-button"
              style={{
                height: isMobile ? '20px' : '1.9vw',
                width: showJoinButton ? (isMobile ? '73px' : '3.5vw') : 'auto',
                paddingLeft: showJoinButton ? (isMobile ? '12px' : '0.52vw') : (isMobile ? '16px' : '0.68vw'),
                paddingRight: showJoinButton ? (isMobile ? '12px' : '0.52vw') : (isMobile ? '16px' : '0.68vw'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '8px' : '0.35vw',
                fontSize: isMobile ? '10px' : '0.65vw',
                fontWeight: 600,
                background: '#FBBF24',
                color: '#161423',
                borderRadius: '30px',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'Inter, -apple-system, system-ui, sans-serif'
              }}
              aria-label={showJoinButton ? "Join Playcast" : "Download Playcast"}
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              {!showJoinButton && <FontAwesomeIcon icon={faDownload} style={{ fontSize: isMobile ? '10px' : '0.62vw' }} />}
              <span>{showJoinButton ? 'Join' : 'Download'}</span>
            </motion.button>

            <div style={{ transform: isMobile ? 'none' : 'scale(1)', display: 'flex', alignItems: 'center' }}>
              <UserButton
                appearance={{
                  elements: {
                    rootBox: 'items-center',
                    userButtonTrigger: isMobile ? 'h-5 focus:!h-5' : 'h-9 focus:!h-9',
                    userButtonAvatarBox: isMobile ? 'size-5' : 'size-9',
                  },
                }}
              >
              <UserButton.MenuItems>
                <UserButton.Link
                  label='Remote Dashboard'
                  labelIcon={<FontAwesomeIcon icon={faComputer} size='sm' />}
                  href={config.DASHBOARD_URL}
                />
              </UserButton.MenuItems>
              <UserButton.UserProfilePage
                label='Privacy'
                url='privacy'
                labelIcon={
                  <div>
                    <FontAwesomeIcon icon={faLock} className='text-md' />
                  </div>
                }
              >
                <div className='h-100 flex flex-col gap-y-2'>
                  <Privacy edition={'pavilion'} />
                </div>
              </UserButton.UserProfilePage>
              </UserButton>
            </div>
          </SignedIn>

          <SignedOut>
            <SignInButton mode="modal">
              <motion.button
                onClick={handleDownloadClick}
                className="aida-button"
                style={{
                height: isMobile ? '20px' : '1.9vw',
                width: 'auto',
                paddingLeft: isMobile ? '16px' : '0.68vw',
                paddingRight: isMobile ? '16px' : '0.68vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '8px' : '0.35vw',
                fontSize: isMobile ? '10px' : '0.65vw',
                  fontWeight: 600,
                  background: '#FBBF24',
                  color: '#161423',
                  borderRadius: '30px',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, system-ui, sans-serif'
                }}
                aria-label={showJoinButton ? "Sign in to join" : "Sign in to download"}
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                {!isMobile && <FontAwesomeIcon icon={faDownload} style={{ fontSize: isMobile ? '10px' : '0.62vw' }} />}
                <span>{showJoinButton ? 'Join' : 'Download'}</span>
              </motion.button>
            </SignInButton>
            
            <SignInButton mode="modal">
              <motion.button
                onClick={() => sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'User Icon', buttonLocation: 'AidaHeader' })}
                className="aida-button"
                style={{
                  height: isMobile ? '20px' : '1.9vw',
                  width: isMobile ? '20px' : '1.9vw',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '10px' : '0.52vw',
                  fontWeight: 600,
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  borderRadius: '50%',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, system-ui, sans-serif'
                }}
                aria-label="Sign in"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <FontAwesomeIcon icon={faUser} />
              </motion.button>
            </SignInButton>
          </SignedOut>

          {/* Hamburger — mobile only */}
          {isMobile && (
            <motion.button
              onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: isMenuOpen ? 'Close Menu' : 'Open Menu', buttonLocation: 'AidaHeader' }); setIsMenuOpen(prev => !prev); }}
              style={{
                width: '20px', height: '20px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(255, 255, 255, 1)', padding: 0, flexShrink: 0,
              }}
              whileTap={{ scale: 0.9 }}
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon icon={isMenuOpen ? faXmark : faBars} style={{ fontSize: '18px' }} />
            </motion.button>
          )}
        </motion.div>
      </div>
    </motion.header>

    {/* Mobile nav drawer — slides in from right */}
    <AnimatePresence>
      {isMobile && isMenuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            style={{
              position: 'fixed', inset: 0, top: '47px',
              backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 398,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Drawer */}
          <motion.nav
            style={{
              position: 'fixed', top: '47px', right: 0, bottom: 0,
              width: '75%', maxWidth: '300px',
              backgroundColor: '#000000', zIndex: 399,
              display: 'flex', flexDirection: 'column',
              padding: '40px 28px', gap: '4px',
              borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
            }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 220 }}
          >
            {[
              { label: 'Home', to: '/' },
              { label: 'For Hosts', to: audience ? `/hosts/${audience}` : '/hosts' },
              { label: 'For Developers', to: '/developers' },
              { label: 'About', to: '/about' },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setIsMenuOpen(false)}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '26px',
                  lineHeight: '60px',
                  color: isActive(item.to) ? '#161423' : 'rgba(255, 255, 255, 1)',
                  textDecoration: 'none',
                  fontVariationSettings: "'wght' 700",
                  letterSpacing: '-0.5px',
                  backgroundColor: isActive(item.to) ? '#FBBF24' : 'transparent',
                  margin: '0 -28px',
                  padding: '0 28px',
                }}
              >
                {item.label}
              </Link>
            ))}
          </motion.nav>
        </>
      )}
    </AnimatePresence>
  </>
  );
};
